import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUserShield, FaFileInvoice, FaBoxes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { getDomainType } from '../utils/domainRouter';
import '../pages/admin/AdminLogin.css';

const Home = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const domainType = getDomainType();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Admin Login State
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: ''
  });
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminShowPassword, setAdminShowPassword] = useState(false);

  // Billing Login State
  const [billingForm, setBillingForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [billingError, setBillingError] = useState('');
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingShowPassword, setBillingShowPassword] = useState(false);
  const [billingShowConfirmPassword, setBillingShowConfirmPassword] = useState(false);
  const [billingIsSignup, setBillingIsSignup] = useState(false);

  // Inventory Login State
  const [inventoryForm, setInventoryForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [inventoryError, setInventoryError] = useState('');
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryShowPassword, setInventoryShowPassword] = useState(false);
  const [inventoryShowConfirmPassword, setInventoryShowConfirmPassword] = useState(false);
  const [inventoryIsSignup, setInventoryIsSignup] = useState(false);

  // Admin Login Handler
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminLoading(true);

    try {
      const result = await login(adminForm.email, adminForm.password);

      if (result.success) {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (userData && userData.role !== 'admin') {
          setAdminError('You are not an admin.');
          setAdminLoading(false);
          return;
        }

        setAdminLoading(false);
        navigate('/adminavsbusiness.com');
      } else {
        setAdminError(result.error || 'Login failed');
        setAdminLoading(false);
      }
    } catch (err) {
      setAdminError(err.response?.data?.message || 'Login failed');
      setAdminLoading(false);
    }
  };

  // Billing Login Handler
  const handleBillingSubmit = async (e) => {
    e.preventDefault();
    setBillingError('');
    setBillingLoading(true);

    try {
      if (billingIsSignup) {
        if (billingForm.password !== billingForm.confirmPassword) {
          setBillingError('Passwords do not match');
          setBillingLoading(false);
          return;
        }

        if (billingForm.password.length < 6) {
          setBillingError('Password must be at least 6 characters long');
          setBillingLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: billingForm.name,
            email: billingForm.email,
            password: billingForm.password,
            role: 'billing'
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setBillingLoading(false);
          setBillingError('');
          alert('Account created successfully! Please login with your credentials.');
          setBillingIsSignup(false);
          setBillingForm({ name: '', email: '', password: '', confirmPassword: '' });
        } else {
          setBillingError(data.message || 'Signup failed');
          setBillingLoading(false);
        }
      } else {
        const result = await login(billingForm.email, billingForm.password);
        if (!result.success) {
          setBillingError(result.error || 'Login failed');
          setBillingLoading(false);
          return;
        }
        const role = result.user?.role || (user && user.role) || JSON.parse(localStorage.getItem('user'))?.role;
        if (role && role !== 'billing' && role !== 'admin') {
          setBillingError('You do not have Billing access.');
          setBillingLoading(false);
          return;
        }
        setBillingLoading(false);
        navigate('/apps.avsbusiness.com', { replace: true });
      }
    } catch (err) {
      setBillingError(err.response?.data?.message || (billingIsSignup ? 'Signup failed' : 'Login failed'));
      setBillingLoading(false);
    }
  };

  // Inventory Login Handler
  const handleInventorySubmit = async (e) => {
    e.preventDefault();
    setInventoryError('');
    setInventoryLoading(true);

    try {
      if (inventoryIsSignup) {
        if (inventoryForm.password !== inventoryForm.confirmPassword) {
          setInventoryError('Passwords do not match');
          setInventoryLoading(false);
          return;
        }

        if (inventoryForm.password.length < 6) {
          setInventoryError('Password must be at least 6 characters long');
          setInventoryLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: inventoryForm.name,
            email: inventoryForm.email,
            password: inventoryForm.password,
            role: 'inventory'
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setInventoryLoading(false);
          setInventoryError('');
          alert('Account created successfully! Please login with your credentials.');
          setInventoryIsSignup(false);
          setInventoryForm({ name: '', email: '', password: '', confirmPassword: '' });
        } else {
          setInventoryError(data.message || 'Signup failed');
          setInventoryLoading(false);
        }
      } else {
        const result = await login(inventoryForm.email, inventoryForm.password);
        if (!result.success) {
          setInventoryError(result.error || 'Login failed');
          setInventoryLoading(false);
          return;
        }
        const role = result.user?.role || (user && user.role) || JSON.parse(localStorage.getItem('user'))?.role;
        if (role && role !== 'inventory' && role !== 'admin') {
          setInventoryError('You do not have Inventory access.');
          setInventoryLoading(false);
          return;
        }
        setInventoryLoading(false);
        navigate('/apps.avsbusiness.com/inventory/dashboard', { replace: true });
      }
    } catch (err) {
      setInventoryError(err.response?.data?.message || (inventoryIsSignup ? 'Signup failed' : 'Login failed'));
      setInventoryLoading(false);
    }
  };

  // Determine which portals to show based on domain
  const getPortalsToShow = () => {
    switch (domainType) {
      case 'admin':
        return ['admin'];
      case 'apps':
        return ['billing', 'inventory'];
      default:
        return ['admin', 'billing', 'inventory'];
    }
  };

  const portals = getPortalsToShow();
  const showAll = portals.length === 3;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '1.5rem' : '2rem',
      fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
    }}>
      <div style={{ textAlign: 'center', maxWidth: '1400px', width: '100%' }}>
        <h1 style={{ fontSize: isMobile ? '32px' : '48px', fontWeight: 800, marginBottom: '16px', color: '#fff', letterSpacing: '0.5px' }}>
          AVS Business Portal
        </h1>
        <p style={{ fontSize: isMobile ? '16px' : '20px', marginBottom: '48px', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
          Choose your portal and log in directly
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: showAll
            ? 'repeat(auto-fit, minmax(320px, 1fr))'
            : portals.length === 2
              ? 'repeat(auto-fit, minmax(320px, 1fr))'
              : '1fr',
          gap: '2rem',
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%'
        }}>
          {/* Admin Login Box */}
          {portals.includes('admin') && (
            <div style={{
              background: '#fff',
              borderRadius: '18px',
              padding: isMobile ? '1.5rem' : '2rem',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              minWidth: isMobile ? '280px' : '320px',
              width: '100%'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <FaUserShield size={48} style={{ color: '#667eea', marginBottom: '12px' }} />
                <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>
                  Admin Portal
                </h2>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  admin.avsbusiness.com
                </p>
              </div>
              <form onSubmit={handleAdminSubmit} className="admin-login-form">
                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    placeholder="Admin Email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                    required
                    className="admin-input"
                  />
                </div>

                <div className="input-wrapper">
                  <input
                    type={adminShowPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Password"
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    required
                    className="admin-input"
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setAdminShowPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {adminShowPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>

                <button type="submit" disabled={adminLoading}>
                  {adminLoading ? 'Logging in...' : 'Login as Admin'}
                </button>
                {adminError && <div className="admin-auth-error">{adminError}</div>}
              </form>
            </div>
          )}

          {/* Billing Login Box */}
          {portals.includes('billing') && (
            <div style={{
              background: '#fff',
              borderRadius: '18px',
              padding: isMobile ? '1.5rem' : '2rem',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              minWidth: isMobile ? '280px' : '320px',
              width: '100%'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <FaFileInvoice size={48} style={{ color: '#667eea', marginBottom: '12px' }} />
                <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>
                  Billing Portal
                </h2>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  apps.avsbusiness.com
                </p>
              </div>
              <form onSubmit={handleBillingSubmit} className="admin-login-form">
                <h3 style={{ marginBottom: '1rem', fontSize: '18px', color: '#333' }}>
                  {billingIsSignup ? 'Billing Sign Up' : 'Billing Login'}
                </h3>

                {billingIsSignup && (
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      value={billingForm.name}
                      onChange={(e) => setBillingForm({ ...billingForm, name: e.target.value })}
                      required
                      className="admin-input"
                    />
                  </div>
                )}

                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    placeholder="Billing Email"
                    value={billingForm.email}
                    onChange={(e) => setBillingForm({ ...billingForm, email: e.target.value })}
                    required
                    className="admin-input"
                  />
                </div>

                <div className="input-wrapper">
                  <input
                    type={billingShowPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Password"
                    value={billingForm.password}
                    onChange={(e) => setBillingForm({ ...billingForm, password: e.target.value })}
                    required
                    className="admin-input"
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setBillingShowPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {billingShowPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>

                {billingIsSignup && (
                  <div className="input-wrapper">
                    <input
                      type={billingShowConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={billingForm.confirmPassword}
                      onChange={(e) => setBillingForm({ ...billingForm, confirmPassword: e.target.value })}
                      required
                      className="admin-input"
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setBillingShowConfirmPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {billingShowConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                    </button>
                  </div>
                )}

                <button type="submit" disabled={billingLoading}>
                  {billingLoading
                    ? (billingIsSignup ? 'Creating Account...' : 'Logging in...')
                    : (billingIsSignup ? 'Sign Up for Billing' : 'Login for Billing')
                  }
                </button>
                {billingError && <div className="admin-auth-error">{billingError}</div>}

                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setBillingIsSignup(!billingIsSignup);
                      setBillingError('');
                      setBillingForm({ name: '', email: '', password: '', confirmPassword: '' });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '14px'
                    }}
                  >
                    {billingIsSignup ? 'Already have an account? Login here' : "Don't have an account? Sign Up here"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Inventory Login Box */}
          {portals.includes('inventory') && (
            <div style={{
              background: '#fff',
              borderRadius: '18px',
              padding: isMobile ? '1.5rem' : '2rem',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              minWidth: isMobile ? '280px' : '320px',
              width: '100%'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <FaBoxes size={48} style={{ color: '#667eea', marginBottom: '12px' }} />
                <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>
                  Inventory Portal
                </h2>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  apps.avsbusiness.com
                </p>
              </div>
              <form onSubmit={handleInventorySubmit} className="admin-login-form">
                <h3 style={{ marginBottom: '1rem', fontSize: '18px', color: '#333' }}>
                  {inventoryIsSignup ? 'Inventory Sign Up' : 'Inventory Login'}
                </h3>

                {inventoryIsSignup && (
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      value={inventoryForm.name}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, name: e.target.value })}
                      required
                      className="admin-input"
                    />
                  </div>
                )}

                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    placeholder="Inventory Email"
                    value={inventoryForm.email}
                    onChange={(e) => setInventoryForm({ ...inventoryForm, email: e.target.value })}
                    required
                    className="admin-input"
                  />
                </div>

                <div className="input-wrapper">
                  <input
                    type={inventoryShowPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Password"
                    value={inventoryForm.password}
                    onChange={(e) => setInventoryForm({ ...inventoryForm, password: e.target.value })}
                    required
                    className="admin-input"
                    style={{ paddingRight: '2.5rem' }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setInventoryShowPassword((prev) => !prev)}
                    tabIndex={-1}
                  >
                    {inventoryShowPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                  </button>
                </div>

                {inventoryIsSignup && (
                  <div className="input-wrapper">
                    <input
                      type={inventoryShowConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      placeholder="Confirm Password"
                      value={inventoryForm.confirmPassword}
                      onChange={(e) => setInventoryForm({ ...inventoryForm, confirmPassword: e.target.value })}
                      required
                      className="admin-input"
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setInventoryShowConfirmPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {inventoryShowConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                    </button>
                  </div>
                )}

                <button type="submit" disabled={inventoryLoading}>
                  {inventoryLoading
                    ? (inventoryIsSignup ? 'Creating Account...' : 'Logging in...')
                    : (inventoryIsSignup ? 'Sign Up for Inventory' : 'Login for Inventory')
                  }
                </button>
                {inventoryError && <div className="admin-auth-error">{inventoryError}</div>}

                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setInventoryIsSignup(!inventoryIsSignup);
                      setInventoryError('');
                      setInventoryForm({ name: '', email: '', password: '', confirmPassword: '' });
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '14px'
                    }}
                  >
                    {inventoryIsSignup ? 'Already have an account? Login here' : "Don't have an account? Sign Up here"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;